import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import FeedbackList from './FeedbackList';
import { useMediaQuery } from "@mui/material";
import axiosInstance from '../axiosInstance';
import { useAtom } from 'jotai';
import { feedbackCountAtom } from '../../helpers/atoms';
import { UserProvider } from '../../Components/UserContext';
import { useUser } from '../../Components/UserContext';

interface FeedbackPageProps { }

const FeedbackPage: React.FC<FeedbackPageProps> = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [currentFeedbackCount, setCurrentFeedbackCount] = useState(0);
  const [deletedFeedbackCount, setDeletedFeedbackCount] = useState(0);
  const [userFeedbackCount, setUserFeedbackCount] = useState(0);
  const { refreshFeedback } = useOutletContext<{ refreshFeedback: boolean }>();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [, setFeedbackCount] = useAtom(feedbackCountAtom);
  const { user } = useUser();

  useEffect(() => {
    const fetchAllCounts = async () => {
      try {
        // Fetch current feedback count
        const currentEndpoint = isAdmin ?
          '/feedback/main-threads?skip=0&limit=10' :
          '/feedback/non-admin-threads?skip=0&limit=10';
        const currentResponse = await axiosInstance.get(currentEndpoint);
        const currentTotal = currentResponse.data.total;
        setCurrentFeedbackCount(currentTotal);
        setFeedbackCount(currentTotal);

        // Fetch user feedback count using user_id from context
        if (user?.user_id) {
          const userFeedbackResponse = await axiosInstance.get(`/feedback/user-threads/${user.user_id}?skip=0&limit=10`);
          setUserFeedbackCount(userFeedbackResponse.data.total);
        }

        // If admin, fetch deleted feedback count
        if (isAdmin) {
          const deletedResponse = await axiosInstance.get('/feedback/historical/main-threads?skip=0&limit=10');
          setDeletedFeedbackCount(deletedResponse.data.total);
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchAllCounts();
  }, [isAdmin, setFeedbackCount, refreshFeedback, user]);

  const handleTabChange = async (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <UserProvider>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', mt: isMobile ? 3 : 0 }}>
        <Box sx={{ width: '100%', position: 'sticky', top: 0, backgroundColor: 'background.default', zIndex: 10 }}>
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label={`Current Feedback (${currentFeedbackCount})`} />
            <Tab label={`My Feedback (${userFeedbackCount})`} />
            {isAdmin && <Tab label={`Deleted Feedback (${deletedFeedbackCount})`} />}
          </Tabs>
        </Box>
        <Box sx={{ width: '85%', margin: 'auto', mt: 4 }}>
          {tabIndex === 0 && (
            <FeedbackList
              refreshFeedback={refreshFeedback}
              variant="current"
              updateCurrentFeedbackCount={setCurrentFeedbackCount}
            />
          )}
          {tabIndex === 1 && (
            <FeedbackList
              refreshFeedback={refreshFeedback}
              variant="user"
              userId={user?.user_id}
              updateCurrentFeedbackCount={setUserFeedbackCount}
            />
          )}
          {tabIndex === 2 && isAdmin && (
            <FeedbackList
              refreshFeedback={refreshFeedback}
              variant="deleted"
              updateDeletedFeedbackCount={setDeletedFeedbackCount}
            />
          )}
        </Box>
      </Box>
    </UserProvider>
  );
};

export default FeedbackPage;
